import { useSuspenseQueries } from '@tanstack/react-query';
import { Suspense } from 'react';

import * as api from '@gitbook/api-client';

import {
    apiQuery,
    getSiteSpacesFromStructure,
    useSiteCapabilityContext,
    useSiteContext,
    useUpgradeSite,
} from '../../../state';
import { APIErrorBoundary } from '../../APIErrorBoundary';
import { SiteStructureEditor } from './SiteStructureEditor';
import { SiteStructureEditSingleSpace } from './SiteStructureEditSingleSpace';
import { SiteStructureEmptyState } from './SiteStructureEmptyState';
/**
 * Site Structure tool in Site settings page
 */
export function SiteStructure() {
    return (
        <APIErrorBoundary
            fallback={{
                404: null,
                403: null,
            }}
        >
            <Suspense fallback={null}>
                <SiteStructureDataLoader />
            </Suspense>
        </APIErrorBoundary>
    );
}

/**
 * Initial data load for the site structure tool
 */
function SiteStructureDataLoader() {
    const { site, organizationId } = useSiteContext();
    const sitePermissions = useSiteCapabilityContext();
    const [{ data: organization }, { data: orgAllContent }, { data: siteStructure }] =
        useSuspenseQueries({
            queries: [
                apiQuery('getOrganizationById', { organizationId }),
                apiQuery('getOrganizationAllContent', { organizationId }),
                apiQuery('getSiteStructure', { organizationId, siteId: site.id }),
            ],
        });
    const upgrade = useUpgradeSite(site, api.SiteFeatureKey.SitesSections);

    const siteSpaces = getSiteSpacesFromStructure(siteStructure);
    const hasGroups =
        siteStructure.type === 'sections' &&
        siteStructure.structure.some((item) => item.object === 'site-section-group');

    if (siteSpaces.length <= 1 && !hasGroups) {
        const singleSiteSpace = siteSpaces[0];
        return (
            <>
                <SiteStructureEmptyState
                    organization={organization}
                    orgAllContent={orgAllContent}
                    siteStructure={siteStructure}
                    site={site}
                    upgrade={upgrade}
                    isEmpty={!singleSiteSpace}
                />
                {singleSiteSpace && sitePermissions.canAdminSite ? (
                    <SiteStructureEditSingleSpace
                        organization={organization}
                        site={site}
                        siteStructure={siteStructure}
                        orgAllContent={orgAllContent}
                        siteSpace={singleSiteSpace}
                    />
                ) : null}
            </>
        );
    }
    return (
        <SiteStructureEditor
            organization={organization}
            orgAllContent={orgAllContent}
            siteStructure={siteStructure}
            site={site}
            sitePermissions={sitePermissions}
            upgrade={upgrade}
        />
    );
}
